import { useEffect } from "react";
import type { QueryClient } from "@tanstack/react-query";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  HeadContent,
  Outlet,
  Scripts,
  createRootRouteWithContext,
  useRouter,
} from "@tanstack/react-router";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { reportLovableError } from "@/lib/lovable-error-reporting";
import appCss from "../styles.css?url";

const organizationSchema = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Método P-nis Liso",
  alternateName: "Smooth P-nis Method",
  url: "https://smothmethod.com/",
  areaServed: { "@type": "Country", name: "Brasil" },
  knowsLanguage: "pt-BR",
  description:
    "Guia baseado em evidências para tratamento de glândulas de Tyson e pápulas peroladas penianas",
  sameAs: [],
});

const websiteSchema = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Método P-nis Liso",
  url: "https://smothmethod.com/",
  inLanguage: "pt-BR",
  description:
    "Visão clínica e guia de tratamento para pápulas peroladas penianas e glândulas de Tyson",
  publisher: { "@type": "Organization", name: "Método P-nis Liso" },
});

const gtagInline =
  "window.dataLayer = window.dataLayer || [];function gtag(){dataLayer.push(arguments);}gtag('js', new Date());gtag('config', 'AW-17824638584');";

const metaPixelInline =
  "!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window, document,'script','https://connect.facebook.net/pt_BR/fbevents.js');fbq('init', '1341291727797938');fbq('init', '2705165196496163');";

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1.0" },
      // Google Search Console verification
      {
        name: "google-site-verification",
        content: "_m2TqEsa-12x3TCOXd-2WE2s5qhwnUNn7Bq9zZIYJPA",
      },
      { title: "Glândulas de Tyson e PPP: Guia Clínico e Remoção (2026)" },
      {
        name: "description",
        content:
          "Guia clínico sobre PPP e glândulas de Tyson: prevalência, diagnóstico diferencial e opções de remoção baseadas em evidências.",
      },
      {
        name: "keywords",
        content:
          "pápulas peroladas penianas, glândulas de tyson, hirsuties papillaris genitalis, remoção de PPP, bolinhas na coroa do p-nis, glândulas sebáceas, manchas de fordyce, eletrocauterização",
      },
      { name: "author", content: "Smooth P-nis Method" },
      {
        name: "robots",
        content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
      },
      // Geo targeting — Brasil
      { name: "geo.region", content: "BR" },
      { name: "geo.placename", content: "Brasil" },
      { name: "language", content: "Portuguese" },
      { name: "target", content: "all" },
      { name: "audience", content: "all" },
      { name: "distribution", content: "global" },
      { httpEquiv: "content-language", content: "pt-br" },
      // Open Graph / Twitter defaults (pages override via Helmet)
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "pt_BR" },
      { property: "og:site_name", content: "Método P-nis Liso" },
      { property: "og:title", content: "Glândulas de Tyson e PPP: Guia Clínico (2026)" },
      {
        property: "og:description",
        content:
          "Guia clínico sobre PPP e glândulas de Tyson: prevalência, diagnóstico diferencial e métodos de remoção comparados.",
      },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Glândulas de Tyson e PPP: Guia Clínico (2026)" },
      {
        name: "twitter:description",
        content:
          "Guia clínico sobre PPP e glândulas de Tyson: prevalência, diagnóstico e métodos de remoção comparados.",
      },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      {
        rel: "icon",
        type: "image/x-icon",
        href: "https://storage.googleapis.com/gpt-engineer-file-uploads/Qk8npHRogsMZtY4Dc4axzk3NEj02/uploads/1766972653457-image (3).jpg",
      },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Dancing+Script:wght@400;500&display=swap",
      },
    ],
    scripts: [
      { type: "application/ld+json", children: organizationSchema },
      { type: "application/ld+json", children: websiteSchema },
      // Google tag (gtag.js)
      { src: "https://www.googletagmanager.com/gtag/js?id=AW-17824638584", async: true },
      { children: gtagInline },
      // Meta Pixel
      { children: metaPixelInline },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFound,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="pt-BR" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <Outlet />
        </TooltipProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-md text-center">
        <h1 className="mb-2 text-xl font-bold text-foreground">This page didn't load</h1>
        <p className="mb-6 text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="flex flex-wrap justify-center gap-2">
          <button
            className="rounded-sm bg-primary px-4 py-2 font-semibold text-primary-foreground"
            onClick={() => {
              router.invalidate();
              reset();
            }}
          >
            Try again
          </button>
          <a
            className="rounded-sm border border-border bg-card px-4 py-2 font-semibold text-foreground"
            href="/"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}